// src/js-dot/frame/patterns/collector.ts
function metaDecorateCollector(option) {
  const {
    class: useClass,
    method: useMethod,
    property: useProperty,
    callback,
    transform
  } = option || {};
  if (!(useClass || useMethod || useProperty)) {
    throw new SyntaxError("Required over then one collection target from class, method or property of option");
  }
  const MAP = /* @__PURE__ */new Map();
  const set = useClass ? useMethod ? useProperty ? function class_method_property(meta, target, propertyKey) {
    if (propertyKey) {
      if (isMethod(target, propertyKey)) {
        MAP.set(target[propertyKey], meta);
      } else {
        MAP.set(target, meta);
      }
    } else {
      MAP.set(target, meta);
    }
  } : function class_method(meta, target, propertyKey) {
    if (propertyKey) {
      if (isMethod(target, propertyKey)) {
        MAP.set(target[propertyKey], meta);
      } else {
        throw new SyntaxError("Unable as property decorator");
      }
    } else {
      MAP.set(target, meta);
    }
  } : useProperty ? function class_property(meta, target, propertyKey) {
    if (propertyKey) {
      if (isMethod(target, propertyKey)) {
        throw new SyntaxError("Unable as method decorator");
      } else {
        MAP.set(target, meta);
      }
    } else {
      MAP.set(target, meta);
    }
  } : function classOnly(meta, target, propertyKey) {
    if (propertyKey) {
      throw new SyntaxError("Unable as method and property decorator");
    }
    MAP.set(target, meta);
  } : useMethod ? useProperty ? function method_property(meta, target, propertyKey) {
    if (!propertyKey) {
      throw new SyntaxError("Unable as class decorator");
    }
    if (isMethod(target, propertyKey)) {
      MAP.set(target[propertyKey], meta);
    } else {
      MAP.set(target, meta);
    }
  } : function methodOnly(meta, target, propertyKey) {
    if (!propertyKey || !isMethod(target, propertyKey)) {
      throw new SyntaxError("Unable as class and property decorator");
    }
    MAP.set(target[propertyKey], meta);
  } : function propertyOnly(meta, target, propertyKey) {
    if (!propertyKey || isMethod(target, propertyKey)) {
      throw new SyntaxError("Unable as class and method decorator");
    }
    MAP.set(target, meta);
  };
  const collector = callback ? transform ? function callback_transform(meta) {
    return function (target, propertyKey) {
      meta = transform(meta);
      set(meta, target, propertyKey);
      callback({
        meta,
        target,
        propertyKey
      });
    };
  } : function callbackOnly(meta) {
    return function (target, propertyKey) {
      set(meta, target, propertyKey);
      callback({
        meta,
        target,
        propertyKey
      });
    };
  } : transform ? function transformOnly(meta) {
    return function (target, propertyKey) {
      set(transform(meta), target, propertyKey);
    };
  } : function collectOnly(meta) {
    return function (target, propertyKey) {
      set(meta, target, propertyKey);
    };
  };
  collector["collection"] = MAP;
  return collector;
  function isMethod(target, propertyKey) {
    return typeof target.constructor.prototype[propertyKey] === "function";
  }
}

// src/js-dot/frame/patterns/rely.ts
import { isEmpty } from "@js-dot/core";

// src/js-dot/frame/frame-log.ts
import { PrimitiveLogger } from "@js-dot/core";
var FRAME_LOG = new PrimitiveLogger({
  rely: "info",
  relyify: "debug",
  reliable: "debug"
});
var FrameLogger = FRAME_LOG;

// src/js-dot/frame/functions/_inspect-token.ts
function inspectToken(token) {
  switch (token) {
    case void 0:
    case null:
      return token + "";
  }
  const type = typeof token;
  switch (type) {
    case "boolean":
      return `Boolean<${token}>`;
    case "number":
      return `Number<${token}>`;
    case "string":
      return `String<${token}>`;
  }
  if (token instanceof RelyToken) {
    return token.toString();
  }
  if (token.constructor === Function) {
    return `${token.name}`;
  }
  return token;
}

// src/js-dot/frame/functions/_inspect-tokens.ts
function inspectTokens(tokens) {
  return tokens.map(inspectToken).join(", ");
}

// src/js-dot/frame/patterns/rely.ts
var {
  isArray: IS_ARRAY
} = Array;
var RELY_CONSTRUCTING_CLASSES = [];
var RELYED_VALUE_MAP = /* @__PURE__ */new Map();
var RELIABLE_MAP = /* @__PURE__ */new Map();
var RELIABLY_PROMISE_RESOLVERS = /* @__PURE__ */new Map();
var PRIMARY_TOKENS = /* @__PURE__ */new Map();
var INDEX = 0;
var RelyToken = class {
  constructor(description) {
    this.description = description;
  }
  [Symbol.toStringTag] = "RelyToken";
  index = INDEX++;
  /**
   * Same as `rely(RelyToken)`
   *
   * @see rely
   */
  get rely() {
    return rely(this);
  }
  /**
   * Same as `reliabled(RelyToken)`
   *
   * @see reliabled
   */
  get reliabled() {
    return reliabled(this);
  }
  /**
   * Same as `reliably(RelyToken)`
   *
   * @see reliably
   */
  get reliably() {
    return reliably(this);
  }
  toString() {
    return `RelyToken<${this.description}(${this.index})>`;
  }
};
function relyToken(description) {
  return new RelyToken(description);
}
function relyify(option) {
  let {
    token
  } = option;
  if (isEmpty(token)) {
    throw new SyntaxError("[js.dev] relyify required token property");
  }
  if (!IS_ARRAY(token)) {
    token = [token];
  }
  FRAME_LOG.relyify(`relyify( ${inspectTokens(token)} )`);
  const primaryToken = token[0];
  if ("value" in option) {
    RELYED_VALUE_MAP.set(primaryToken, option.value);
    return;
  }
  if (!option.factory && !option.class && !option.value) {
    switch (typeof primaryToken) {
      case "object":
      case "function":
        break;
      default:
        throw new SyntaxError("[js.dev] If factory, class and value is null. using first to make reliable class, So first should be a constructor");
    }
    option.class = primaryToken;
  }
  if (option.multiple) {
    if (RELIABLE_MAP.has(primaryToken)) {
      const values = RELIABLE_MAP.get(primaryToken);
      if (!Array.isArray(values)) {
        throw new SyntaxError(`[js.dev] ${primaryToken} was rely-ify-ed as not a multiple first already`);
      }
      values[values.length] = primaryToken;
    } else {
      RELIABLE_MAP.set(primaryToken, [option]);
    }
  } else {
    if (!option.class && RELIABLE_MAP.has(primaryToken)) {
      throw new SyntaxError(`[js.dev] ${primaryToken} is not a multiple first`);
    }
    RELIABLE_MAP.set(primaryToken, option);
  }
  if (RELIABLY_PROMISE_RESOLVERS.has(primaryToken)) {
    const relied = rely(primaryToken);
    const item = RELIABLY_PROMISE_RESOLVERS.get(primaryToken);
    if (item) {
      for (const resolve of item.values()) {
        resolve(relied);
      }
      RELIABLY_PROMISE_RESOLVERS.delete(primaryToken);
    }
  }
  let current = token.length;
  while (current-- > 0) {
    PRIMARY_TOKENS.set(token[current], primaryToken);
  }
}
function rely(token, args) {
  switch (token) {
    case void 0:
    case null:
    case true:
    case false:
      throw new RangeError(`[js.dev] rely token should not be a undefined, null and boolean`);
  }
  let primaryToken = PRIMARY_TOKENS.get(token);
  if (!primaryToken) {
    primaryToken = token;
  }
  if (RELYED_VALUE_MAP.has(primaryToken)) {
    return RELYED_VALUE_MAP.get(primaryToken);
  }
  const relyingIndex = RELY_CONSTRUCTING_CLASSES.length;
  if (RELY_CONSTRUCTING_CLASSES.includes(primaryToken)) {
    throw new SyntaxError(`[js.dev] ${inspectRelyingPath(primaryToken)} in Recursive Injection`);
  }
  let result;
  let target = primaryToken;
  if (primaryToken instanceof RelyToken || typeof primaryToken === "string") {
    if (!reliabled(primaryToken)) {
      const error = new SyntaxError(`[.dev] ${primaryToken} is not a reliable token`);
      globalThis["process"] && console.debug(error.stack);
      throw error;
    }
    const reliableTarget = RELIABLE_MAP.get(primaryToken);
    if (target.factory) {
      result = target.factory();
      if (!reliableTarget.multiple) {
        RELYED_VALUE_MAP.set(primaryToken, result);
      }
      return result;
    }
    target = reliableTarget.class;
  }
  RELY_CONSTRUCTING_CLASSES[relyingIndex] = primaryToken;
  FRAME_LOG.rely("rely(", inspectRelyingPath(), ")");
  if (IS_ARRAY(args)) {
    result = new target(...args);
  } else if (!target.length) {
    result = new target();
  } else {
    throw new SyntaxError(`[.dev] ${inspectRelyingPath()} Required ${target.length} Arguments for first relying`);
  }
  RELY_CONSTRUCTING_CLASSES.pop();
  RELYED_VALUE_MAP.set(primaryToken, result);
  return result;
}
function reliabled(token) {
  return RELIABLE_MAP.has(PRIMARY_TOKENS.get(token));
}
function relyifiable(token_or_tokens) {
  const tokens = IS_ARRAY(token_or_tokens) ? token_or_tokens : [token_or_tokens];
  let current = tokens.length;
  while (current-- > 0) {
    const token = tokens[current];
    const primaryToken = PRIMARY_TOKENS.get(token);
    if (RELIABLE_MAP.has(primaryToken) && !RELIABLE_MAP.get(primaryToken).override) {
      return false;
    }
  }
  return true;
}
function reliably(token) {
  if (Array.isArray(token)) {
    const promises = [];
    const end = token.length;
    let current = -1;
    while (++current < end) {
      promises[promises.length] = reliably(token[current]);
    }
    return Promise.all(promises);
  }
  if (reliabled(token)) {
    return Promise.resolve(rely(token));
  }
  return new Promise(resolve => {
    if (RELIABLY_PROMISE_RESOLVERS.has(token)) {
      RELIABLY_PROMISE_RESOLVERS.get(token)?.add(resolve);
    } else {
      RELIABLY_PROMISE_RESOLVERS.set(token, /* @__PURE__ */new Set([resolve]));
    }
  });
}
var relify = relyify;
var relyabled = reliabled;
var relyably = reliably;
function inspectRelyingPath(currentRelyTarget) {
  if (currentRelyTarget) {
    RELY_CONSTRUCTING_CLASSES[RELY_CONSTRUCTING_CLASSES.length] = currentRelyTarget;
  }
  return RELY_CONSTRUCTING_CLASSES.map(inspectToken).reverse().join(" <- ");
}

// src/js-dot/frame/decorators/reliable.ts
function reliable(option) {
  return function reliableMetaDecorator(target, context) {
    const stableOption = option && option.constructor !== Object ? {
      token: option
    } : option || {};
    if (!("selfTokenize" in stableOption)) {
      stableOption["selfTokenize"] = true;
    }
    const tokens = stableOption?.token ? Array.isArray(stableOption.token) ? stableOption.token : [stableOption.token] : [];
    if (stableOption.selfTokenize && tokens.indexOf(target) < 0) {
      tokens[tokens.length] = target;
    } else if (!tokens.length) {
      throw new ReferenceError(`Should be set a token when selfTokenize is false.`);
    }
    if (relyifiable(tokens)) {
      FRAME_LOG.reliable(`@reliable( ${inspectTokens(tokens)} )`);
      stableOption.token = tokens;
      stableOption.class = target;
      relyify(stableOption);
    } else {
      FRAME_LOG.reliable(`@reliable(${inspectTokens(tokens)}) was fail with tokens already rely-ify-ed`);
    }
  };
}

// src/js-dot/frame/functions/semi-markdown.ts
var md_bold_asterisk = /\*\*((?:(?!\*\*)|.)+)\*\*/;
var md_bold_underbar = /__((?:(?!__)|.)+)__/;
var md_italic_asterisk = /\*([^*]+)\*/;
var md_italic_underbar = /_([^_]+)_/;
function semiMarkdown(text) {
  return text.replace(md_bold_asterisk, "<em>$1</em>").replace(md_bold_underbar, "<b>$1</b>").replace(md_italic_asterisk, "<i>$1</i>").replace(md_italic_underbar, "<i>$1</i>");
}
export { FRAME_LOG, FrameLogger, RelyToken, metaDecorateCollector, reliable, reliabled, reliably, relify, rely, relyToken, relyabled, relyably, relyifiable, relyify, semiMarkdown };
